// ═══════════════════════════════════════════════════════════════════════════
//  LiveTrading — shared foundation: endpoints, symbol universes, types, and the
//  small pure helpers the page and its panels both use.
//
//  Lifted verbatim out of LiveTrading.tsx (God 2026-08-26) so a 6,928-line file
//  stops being the only place to look. Nothing here was rewritten in the move;
//  it is the same code at a smaller address.
//
//  This module must never import from LiveTrading.tsx or from a panel. It is the
//  bottom of the dependency graph, and keeping it there is what stops the import
//  cycle that would otherwise hand one side a half-initialised module at runtime.
// ═══════════════════════════════════════════════════════════════════════════

import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { FLEET as FLEET_REGISTRY, botOf } from '../botIdentity';
import { useManagedSymbols, managedApiSym } from '../lib/stores/managedSymbols';
import { botLabel, botSource, optLabel } from '../tradeAnalytics';
import FleetRegistryCards from '../components/FleetRegistryCards';
import CasHoldingGates from '../components/CasHoldingGates';
import { recordDepthSnap, analyzeBot } from '../services/botMemory';
import { GranularPositionManager } from '../components/GranularPositionManager';
import { ContestReadiness } from '../components/ContestReadiness';
import { AccSection, useAccordion } from '../components/ui/Smart';

export const API_BASE_URL = 'https://api.jaiswalcapital.com';
export const WEBHOOK_BASE_URL   = `${API_BASE_URL}/webhook/tv`;
export const KEEP_ALIVE_URL     = `${API_BASE_URL}/health`;
export const TRADES_SUMMARY_URL = `${API_BASE_URL}/trades/summary`;
export const TRADES_TODAY_URL   = `${API_BASE_URL}/trades/today`;
export const TRADES_OPEN_URL    = `${API_BASE_URL}/trades/open`;

// ── SESSION DAY, not calendar day (God 2026-09-24 00:4x) ─────────────────────
// Every "today" tile read 0 at 00:38 on the 24th while the cards still carried
// the 22nd: the page keyed on the IST calendar date, so from midnight until the
// 09:00 bell the dashboard showed a day that had not happened yet. The SESSION
// is today once the market has opened (09:00 IST, Mon-Fri) and the last
// weekday otherwise. Server queries (bot-stats, signals/log) take this date.
export const sessionIso = (now: Date = new Date()): string => {
  const ist = new Date(now.getTime() + 5.5 * 3600 * 1000);
  const dow = ist.getUTCDay();
  const mins = ist.getUTCHours() * 60 + ist.getUTCMinutes();
  if (dow >= 1 && dow <= 5 && mins >= 9 * 60) return ist.toISOString().slice(0, 10);
  let d = new Date(ist.getTime() - 86400000);
  while (d.getUTCDay() === 0 || d.getUTCDay() === 6) d = new Date(d.getTime() - 86400000);
  return d.toISOString().slice(0, 10);
};
export const isSessionToday = (now: Date = new Date()): boolean =>
  sessionIso(now) === new Date(now.getTime() + 5.5 * 3600 * 1000).toISOString().slice(0, 10);
/** "Today" while the session is the calendar day, else the session's date ("23 Sep"). */
export const sessionLabel = (now: Date = new Date()): string => {
  if (isSessionToday(now)) return 'Today';
  const [y, m, d] = sessionIso(now).split('-').map(Number);
  return new Date(Date.UTC(y, m - 1, d)).toLocaleDateString('en-IN', { day: 'numeric', month: 'short', timeZone: 'UTC' });
};
export const LOT_SIZES_URL      = `${API_BASE_URL}/lot-sizes`;
export const ATM_URL            = `${API_BASE_URL}/atm`;
export const EXPIRIES_URL       = `${API_BASE_URL}/expiries`;
export const OPTION_CHAIN_URL   = `${API_BASE_URL}/option-chain`;          // 🆕 v6.0
export const OPTION_LTP_URL     = `${API_BASE_URL}/option-ltp`;            // 🆕 v6.0 — get specific CE/PE price
export const SIGNALS_RECENT_URL = `${API_BASE_URL}/signals/recent`;        // 🆕 v6.0 — last 20 webhook signals

// ⭐ v11.0 NEW — Direct Dhan order execution + market depth WebSocket
// DIRECT_ORDER_URL removed — UI never places orders; signals go via /webhook/tv router
export const DEPTH_WS_URL       = 'wss://api.jaiswalcapital.com/ws/depth'; // 20-level order book - ONE STREAM fan-out (God 2026-10-24; legacy {type:subscribe} protocol accepted)
export const SIGNALS_WS_URL     = 'wss://api.jaiswalcapital.com/ws/signals';      // real-time signal push

export const KEEP_ALIVE_INTERVAL    = 10 * 60 * 1000;
export const BACKEND_SYNC_INTERVAL  = 3 * 1000;
// 5s (tha 1s): 1s par ye poll ~12 symbols/sec /atm bhejta tha → nginx api_limit
// trip (excess 60+) + 2-core Python API choke → dashboard "Backend offline"
// flaps (2026-07-13 13:47 incident). Ye sirf DISPLAY spot hai — execution
// pricing server-side hoti hai; 5s freshness kaafi hai.
export const SPOT_REFRESH_INTERVAL  = 5 * 1000;
// God 2026-09-21: the dashboard's spot prices now ride the shared
// /ws/live-feed PUSH (see LiveTrading refreshSpots). This REST loop is a
// FALLBACK for symbols the fast lane does not carry, so it runs slowly on
// purpose - a tighter poll under a live push buys nothing and is exactly
// what fed the Dhan 429 stampede (RULE #22).
export const SPOT_REFRESH_FALLBACK_MS = 30 * 1000;
export const OPTION_CHAIN_INTERVAL  = 5 * 1000;       // 5s — backend cache is 3s; 1s was causing interval restarts
export const SIGNAL_STREAM_INTERVAL = 2 * 1000;       // 🆕 v6.0

export const NIFTY_50_FNO_LIST = [
  'RELIANCE','HDFCBANK','ICICIBANK','INFY','TCS','BHARTIARTL','ITC','LT','SBIN',
  'AXISBANK','KOTAKBANK','HINDUNILVR','BAJFINANCE','ASIANPAINT','MARUTI','HCLTECH',
  'SUNPHARMA','TITAN','ULTRACEMCO','NTPC','NESTLEIND','ONGC','WIPRO','POWERGRID',
  'M&M','ADANIENT','TATAMOTORS','JSWSTEEL','BAJAJFINSV','COALINDIA','TECHM',
  'TATASTEEL','GRASIM','INDUSINDBK','DRREDDY','CIPLA','HINDALCO','BPCL',
  'EICHERMOT','HEROMOTOCO','DIVISLAB','BRITANNIA','APOLLOHOSP','BAJAJ-AUTO',
  'SBILIFE','HDFCLIFE','ADANIPORTS','TATACONSUM','LTIM','SHRIRAMFIN'
];

// Nifty Next-50 constituents (48 are F&O; BERGEPAINT/IRCTC are cash-only — the
// backend resolves their lot via the F&O map or falls back to 1 for cash equity).
export const NIFTY_NEXT_50_LIST = [
  'ABB','ADANIENSOL','ADANIGREEN','ADANIPOWER','AMBUJACEM','BAJAJHLDNG','BANKBARODA',
  'BERGEPAINT','BOSCHLTD','CANBK','CGPOWER','CHOLAFIN','COLPAL','DABUR','DIVISLAB','DLF',
  'DMART','GAIL','GODREJCP','HAVELLS','HAL','ICICIGI','ICICIPRULI','INDIGO','IOC','IRCTC',
  'IRFC','JINDALSTEL','JSWENERGY','LICI','LODHA','MARICO','MOTHERSON','MPHASIS','MUTHOOTFIN',
  'NAUKRI','NMDC','PFC','PIDILITIND','PNB','RECLTD','SHREECEM','SIEMENS','SRF','TATAPOWER',
  'TORNTPHARM','TVSMOTOR','UNITDSPR','VBL','ZYDUSLIFE'
];
// All Nifty-50 + Next-50 stocks for the Smart Asset Builder quick-add (lot auto-resolved).
export const ALL_FNO_STOCKS = [...NIFTY_50_FNO_LIST, ...NIFTY_NEXT_50_LIST];

export const INDICES_LIST = ['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX','BANKEX'];
// Liquid MCX commodities (lot/contract size from the Node instrument master).
// ZINC/ALUMINIUM/LEAD removed — no backend data feed (deep-check 2026-07-04).
export const COMMODITIES_LIST = ['GOLD','GOLDM','SILVER','SILVERM','SILVERMIC','CRUDEOIL','CRUDEOILM','NATURALGAS','COPPER'];
export const ETFS = ['NIFTYBEES','BANKBEES','GOLDBEES','SILVERBEES','JUNIORBEES','LIQUIDBEES','ITBEES','PSUBNKBEES'];

// ── ACTIVE (PASS-hui) SYMBOLS — server SSOT (God 2026-07-14) ────────────────
// Depth/Pricer ke symbol-chips sirf active universe tak (deployments RUNNING +
// registry ACTIVE + botversions tags + extras — wahi jo capture/GC use karta
// hai, /api/active-symbols se). Fail-open: fetch fail → null → full catalogs.
export const _normSym = (s: string) => String(s || '').toUpperCase().replace(/[&\-\s]+/g, '_').replace(/^_+|_+$/g, '');
let _activeSymsCache: { at: number; set: Set<string> | null } = { at: 0, set: null };
async function fetchActiveSymbols(): Promise<Set<string> | null> {
  if (_activeSymsCache.set && Date.now() - _activeSymsCache.at < 300000) return _activeSymsCache.set;
  try {
    const r = await fetch(`${API_BASE_URL}/api/active-symbols`, { credentials: 'include' });
    const j = await r.json();
    if (j?.status !== 'success') return _activeSymsCache.set;
    const set = new Set<string>(
      [...(j.indices || []), ...(j.stocks || []), ...(j.mcx || []), ...(j.etfs || [])].map(_normSym));
    _activeSymsCache = { at: Date.now(), set };
    return set;
  } catch { return _activeSymsCache.set; }
}
export const useActiveSymbols = (open: boolean) => {
  const [act, setAct] = useState<Set<string> | null>(_activeSymsCache.set);
  useEffect(() => { if (open) fetchActiveSymbols().then(s => setAct(s)); }, [open]);
  return act;
};
// Bot ka deployment-symbol → panel-catalog spelling ("NIFTY 50"/"NIFTY_50" → "NIFTY")
export const toPanelSym = (s: string) => {
  const n = _normSym(s);
  const IDX: Record<string, string> = { NIFTY_50: 'NIFTY', NIFTY50: 'NIFTY', BANK_NIFTY: 'BANKNIFTY',
    FIN_NIFTY: 'FINNIFTY', MIDCAP_NIFTY: 'MIDCPNIFTY' };
  return IDX[n] || String(s || '').toUpperCase().trim();
};

export const DEFAULT_LOT_META: Record<string, { lot_size: number; strike_step: number }> = {
  // NSE Index options — checked against the broker's own scrip master
  // (SEM_LOT_UNITS, every live expiry) on 2026-09-21 after God saw the console
  // arguing with itself: NIFTY was 75 here and 65 in the master, FINNIFTY 65
  // here / 60 in the master. The ENGINE has had the right numbers since
  // 2026-07-15 (routes_trading._LOT_BY_UNDERLYING), so the dashboard was the
  // only place still sizing off the old pre-revision lots. These are a
  // FALLBACK only — /lot-sizes now serves the engine's live table.
  NIFTY:      { lot_size: 65,  strike_step: 50  },
  BANKNIFTY:  { lot_size: 30,  strike_step: 100 },
  FINNIFTY:   { lot_size: 60,  strike_step: 50  },
  MIDCPNIFTY: { lot_size: 120, strike_step: 25  },
  SENSEX:     { lot_size: 20,  strike_step: 100 },
  BANKEX:     { lot_size: 30,  strike_step: 100 },
  // MCX Commodity futures & options — LOT UNITS, not contract size (God
  // 2026-09-21). These held 1 for GOLD/SILVER/COPPER because someone wrote down
  // "1 kg per lot" and "1 MT per lot", which describes the CONTRACT, not the
  // quantity an order carries. The engine has traded 100/30/2500 all along, so
  // the console warned on five symbols every refresh and the fallback would
  // have sized a GOLD position 100x too small if the server were ever down.
  GOLD:       { lot_size: 100,  strike_step: 100 },
  GOLDM:      { lot_size: 10,   strike_step: 50  },
  SILVER:     { lot_size: 30,   strike_step: 500 },
  SILVERM:    { lot_size: 5,    strike_step: 100 },
  SILVERMIC:  { lot_size: 1,    strike_step: 50  },
  CRUDEOIL:   { lot_size: 100,  strike_step: 50  },
  CRUDEOILM:  { lot_size: 10,   strike_step: 25  },
  NATURALGAS: { lot_size: 1250, strike_step: 5   },
  COPPER:     { lot_size: 2500, strike_step: 5   }
};

export const BROKERS = [
  { id: 'ZERODHA', name: 'Zerodha Kite',        color: '#387ed1' },
  { id: 'DHAN',    name: 'Dhan HQ',             color: '#4ade80' },
  { id: 'UPSTOX',  name: 'Upstox Pro',          color: '#9333ea' },
  { id: 'ANGEL',   name: 'Angel One SmartAPI',  color: '#ef4444' },
  { id: 'FYERS',   name: 'Fyers API',           color: '#fbbf24' },
  { id: 'ALICE',   name: 'Alice Blue ANT',      color: '#06b6d4' },
];

export interface TargetAsset {
  symbol: string; type: 'INDEX'|'STOCK'|'OPTION'|'ETF'|'COMMODITY'|'FUTURE';
  enabled?: boolean;   // per-symbol ON/OFF — false = engine skips this leg
  // Dual-mode per symbol: overrides the bot-level tradingMode for THIS leg
  // (absent = inherit bot). Engine resolves it signal-by-signal server-side.
  tradingMode?: 'PAPER'|'LIVE';
  qty: number; lots: number; lotSize: number; sl: number; target: number;
  trailingSlPts: number; strike?: string;
  manualRiskEnabled?: boolean;   // OFF (default) = Pine code handles exits (Rudra DNA safe); ON = platform applies SL/Target/Trailing
  strikeMode?: 'ATM'|'ITM1'|'ITM2'|'ITM3'|'OTM1'|'OTM2'|'OTM3'|'MANUAL';
  optType?: 'CE'|'PE'; expiry?: string; segment: string;
  autoSelected?: boolean; baseSymbol?: string;
  // ⏱ PER-SYMBOL timeframe — har symbol apne best TF par (empty = bot-level TF).
  // Same unified field as the Mamta AI Bot Configuration modal (asset.timeframe).
  timeframe?: string;
  // 🕔 Trishul temporal windows — subset = only those ON; absent = always ON.
  zones?: string[];
  // 👥 PER-SYMBOL CLIENT ROUTING (God 2026-07-27): overrides the bot-level
  // routing matrix for THIS symbol only. Per client: enabled true/false
  // (absent = inherit bot-level) + optional qtyMult override. Consumed by
  // erp_resolver.resolve_fanout — Client A sirf apne chune symbols trade karta hai.
  clientRouting?: Record<string, { enabled?: boolean; qtyMult?: number }>;
}

// ⏱ Timeframe selection — same single-source flow as Bot Configuration:
// selected TF → SuperChart bot view (jc_chart_bot_ctx) + engine algo deploy.
export const LT_TF_CHOICES = ['1s', '5s', '15s', '1m', '3m', '5m', '15m', '30m', '1H', '4H', '1D'];

export const LT_TF_HOME: Record<string, string[]> = {
  VEG:     ['1s', '5s', '15s', '1m', '5m'],   // options scalp
  SHOOL:   ['5m', '15m', '1H'],               // futures short-term
  STHAMBH: ['15m', '4H', '1D'],               // cash/ETF long hold
};

export interface Deployment {
  id: string; name: string; assets: TargetAsset[]; model: string;
  side: 'LONG ONLY'|'SHORT ONLY'|'BOTH'; orderType: 'MARKET'|'LIMIT'|'SL'|'SL-M';
  product: 'MIS'|'NRML'|'CNC'; targetClients: string[]; targetBrokers: string[];
  capitalAllocatedPct: number; maxLossPerDay: number; activationTime: string;
  autoStopTime: string; reEntryEnabled: boolean; reEntryCount: number;
  trailingSlEnabled: boolean; squareOffOnTarget: boolean;
  tradeWindows?: { start: string; end: string; enabled: boolean }[];   // multi ON/OFF entry windows
  status: 'RUNNING'|'STOPPED'|'ERROR'|'PAUSED'|'WARMING';
  pnl: number; trades: number; wins: number; losses: number;
  lastSignal: string; startedAt: string; webhookEnabled: boolean;
  webhookUrl: string; apiKey: string; apiSecret: string;
  autoStrikeEnabled: boolean; autoFireEnabled: boolean; tradingMode: 'PAPER'|'LIVE';
  deployTarget?: 'TRADINGVIEW'|'SUPERCHART'|'BOTH';   // where this bot's signals come from
  timeframe?: string;   // ⏱ bot-level default TF — chart + algo deploy (unified record)
  // 🎛 MULTI-CLIENT × MULTI-BROKER routing matrix — per-client override for THIS
  // bot: ON/OFF, broker route (AUTO = bot ke targetBrokers me se jo client ke
  // paas hai), qty multiplier. Unified record me save hota hai (engine fan-out).
  clientRouting?: Record<string, { enabled?: boolean; broker?: string; qtyMult?: number }>;
}

export interface TradeHistory {
  id: string; tradeId?: string; time: string; date: string; dateIso?: string;
  symbol: string; side: 'BUY'|'SELL';
  qty: number; entryPrice: number; exitPrice: number; pnl: number;
  status: 'WIN'|'LOSS'|'BREAKEVEN'; mode: 'PAPER'|'LIVE'; broker: string; client: string;
  botId: string; botName: string; source?: string;
  entryTime?: string; exitTime?: string;
  timeframe?: string;   // ⏱ signal TF (1s/5s/1m…) — God 2026-07-15 multi-TF orders
}

export interface Log {
  id: string; time: string; system: string; message: string;
  type: 'INFO'|'SUCCESS'|'WARNING'|'ERROR'|'TRADE';
}

export interface FleetClient {
  id: string; name: string; capital: number; broker: string; brokerId: string; connected: boolean;
}

export interface InstrumentMeta { lot_size: number; strike_step: number; type?: string; }

// 🆕 v7.0 — Unified Bot Bridge (cross-component sync)
export interface MamtaBotBridge {
  id: string;
  name: string;
  module: string;            // VEG / SHOOL / STHAMBHAN / etc
  symbol: string;
  tf: string;
  riskPct: number;
  status: 'paper'|'live'|'stopped';
  pnl: number;
  trades: number;
  wins: number;
  losses: number;
  source: 'mamta_ai'|'manual';
  syncedAt: number;
}

export const BRIDGE_KEY = 'jc_mamta_bots';   // ← MamtaAiv7 writes, LiveTrading reads

// Read the Mamta bots bridge as a JSON string. Prefers the dedicated bridge key,
// but falls back to MamtaAiv6's always-persisted 'jc_mamta_bots_v1' (the full Bot[])
// and converts it — so deployed bots appear in the hub AUTOMATICALLY even if the
// user hasn't opened MAMTA AI PRO since this session (no manual step needed).
export function readMamtaBridgeRaw(): string {
  try {
    const main = localStorage.getItem(BRIDGE_KEY);
    if (main) { const a = JSON.parse(main); if (Array.isArray(a) && a.length) return main; }
    const v1 = localStorage.getItem('jc_mamta_bots_v1');
    if (v1) {
      const bots = JSON.parse(v1);
      if (Array.isArray(bots)) {
        const conv = bots
          .filter((b: any) => b && b.status !== 'stopped')
          .map((b: any) => ({
            id: b.id, name: b.name, module: String(b.id || '').toUpperCase(),
            symbol: b.symbol, tf: b.tf, riskPct: b.riskPct ?? 1, status: b.status,
            pnl: b.pnl ?? 0, trades: b.trades ?? 0, wins: b.wins ?? 0,
            losses: Math.max(0, (b.trades ?? 0) - (b.wins ?? 0)),
            source: 'mamta_ai', syncedAt: Date.now(),
          }));
        if (conv.length) return JSON.stringify(conv);
      }
    }
  } catch { /* ignore */ }
  return '';
}

// 🆕 v6.0 — Live Option Chain Row
export interface OptionChainRow {
  strike: number;
  ce_ltp: number; ce_oi: number; ce_volume: number; ce_iv?: number;
  pe_ltp: number; pe_oi: number; pe_volume: number; pe_iv?: number;
  is_atm?: boolean;
}

// 🆕 v6.0 — Live Signal Stream
export interface LiveSignal {
  id: string;
  time: string;
  bot_id: string;
  bot_name: string;
  action: string;
  comment: string;
  symbol: string;
  option_symbol?: string;
  strike?: number;
  opt_type?: 'CE'|'PE';
  spot: number;
  ltp: number;          // option premium LTP
  status: 'PENDING'|'EXECUTED'|'FAILED';
  pnl?: number;
}

// ⭐ v11.0 — Market Depth (order book; 20-level on NSE, 5-level on MCX)
export interface DepthLevel { price: number; qty: number; orders?: number; }
export interface MarketDepthData {
  symbol: string;
  bids: DepthLevel[];   // sorted desc — best bid first
  asks: DepthLevel[];   // sorted asc  — best ask first
  ltp?: number; spread?: number; timestamp?: string;
  // God 2026-08-26 — the book must state its own depth. Dhan enables Full
  // (20-level) Depth for NSE equity/derivatives ONLY; MCX comes back as a
  // 5-level REST quote. Rendering a fixed 20 rows over a 5-level book left 15
  // blank rows that read as a dead feed, so the panel now sizes itself.
  depthLevels?: number;             // levels this book actually carries
  source?: string;                  // 'twentydepth' | 'rest_quote_5level' | 'rest5'
  crossed?: boolean;                // bid >= ask — pre-open auction / stale mix
}

// The 3 Rudra bots (ids match MamtaAiv6 bot ids, uppercased — so imported
// bridge bots resolve their icon/colour) + WEBHOOK (external) = 4 trading models.
export const AI_MODELS = [
  { id:'VEG',      icon:'⚡',  col:'#22d3ee', wr:'62.0%', style:'OPTIONS SCALP' },
  { id:'SHOOL',    icon:'🔱', col:'#ef4444', wr:'59.8%', style:'FUTURES 1-60D' },
  { id:'STHAMBH',  icon:'🏛️', col:'#8b5cf6', wr:'75.0%', style:'CASH LONG'     },
  { id:'WEBHOOK',  icon:'📡', col:'#a855f7', wr:'N/A',   style:'EXTERNAL'      },
];

export const fmtM = (n: number) => Math.abs(n) >= 100000 ? (n/100000).toFixed(2)+'L' :
  Math.abs(n) >= 1000 ? (n/1000).toFixed(1)+'K' : n.toFixed(0);
export const pCol = (n: number) => n >= 0 ? '#10b981' : '#f87171';
export const pSgn = (n: number) => n >= 0 ? '+' : '';
// Trade-wise P&L % = net P&L / capital deployed (premium outlay for options, margin
// for futures). Falls back to entry×qty when capitalUsed isn't stamped (God 2026-07-28).
export const pnlPct = (t: any): number => {
  const cap = Number(t?.capitalUsed) > 0 ? Number(t.capitalUsed)
    : (Number(t?.entryPrice) > 0 && Number(t?.qty) > 0 ? Number(t.entryPrice) * Number(t.qty) : 0);
  return cap > 0 ? (Number(t?.pnl || 0) / cap) * 100 : 0;
};
// ONE time format across the report — buy & sell both shown as 24h HH:mm:ss.
// Backend mixes formats: bot exits write "13:15:27" (Python strftime, 24h) while
// square-offs write "1:23:36 pm" (Node toLocaleTimeString, 12h). Normalize here so
// every row matches (also covers all historical rows already stored mixed).
export const fmtTime = (raw?: string): string => {
  const s = String(raw || '').trim();
  if (!s) return '—';
  const m = s.match(/(\d{1,2}):(\d{2})(?::(\d{2}))?\s*([ap]\.?m\.?)?/i);
  if (!m) return s;
  let h = parseInt(m[1], 10);
  const min = m[2], sec = m[3] || '00';
  const ap = (m[4] || '').toLowerCase().replace(/\./g, '');
  if (ap === 'pm' && h < 12) h += 12;
  if (ap === 'am' && h === 12) h = 0;
  return `${String(h).padStart(2, '0')}:${min}:${sec}`;
};
// AbortSignal.timeout() polyfill — 2022+ feature. Falls back to manual
// AbortController so older browsers don't throw `undefined.timeout()`.
export function fetchTimeout(ms: number): { signal: AbortSignal; clear: () => void } {
  const ctl = new AbortController();
  const t = setTimeout(() => ctl.abort(), ms);
  return { signal: ctl.signal, clear: () => clearTimeout(t) };
}

// Singleton AudioContext — was previously created on every beep, leaking
// memory across a trading day. Modern browsers also forbid creation outside
// a user gesture; we lazy-init on first call and reuse forever.
let _ltAudioCtx: AudioContext | null = null;
export function ltBeep(freq = 880, dur = 0.15) {
  try {
    if (!_ltAudioCtx) {
      const Ctor = window.AudioContext || (window as unknown as { webkitAudioContext: typeof AudioContext }).webkitAudioContext;
      if (!Ctor) return;
      _ltAudioCtx = new Ctor();
    }
    const o = _ltAudioCtx.createOscillator();
    const g = _ltAudioCtx.createGain();
    o.connect(g); g.connect(_ltAudioCtx.destination);
    o.frequency.value = freq; g.gain.value = 0.05;
    o.start(); o.stop(_ltAudioCtx.currentTime + dur);
  } catch { /* user-gesture restriction */ }
}

// Module-level hook: setInterval that pauses when the browser tab is hidden.
// LiveTrading runs 8+ concurrent polls (RUDRA state, autoloop, smart slots,
// memory stats, signals, backend sync, spots, bridge), all of which used to
// keep hammering the backend even in a background tab.
export function usePausableInterval(fn: () => void, ms: number, deps: React.DependencyList, enabled = true) {
  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => {
    if (!enabled) return;
    let timer: ReturnType<typeof setInterval> | null = null;
    const start = () => {
      if (timer) clearInterval(timer);
      timer = setInterval(fn, ms);
    };
    const stop = () => { if (timer) { clearInterval(timer); timer = null; } };
    const onVis = () => { if (document.hidden) stop(); else { fn(); start(); } };
    if (!document.hidden) start();
    document.addEventListener('visibilitychange', onVis);
    return () => { stop(); document.removeEventListener('visibilitychange', onVis); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [enabled, ms, ...deps]);
}

// Cryptographically-secure API key generator. Previously used Math.random()
// which is predictable — every install shipped the SAME default WEBHOOK key
// below, so anyone who had read the bundled source could fire signals to any
// user's bot. We now generate per-install on first run and seed from
// `crypto.getRandomValues` (true browser CSPRNG).
export const generateApiKey = () => {
  const arr = new Uint8Array(18);
  (window.crypto || (window as unknown as { msCrypto: Crypto }).msCrypto).getRandomValues(arr);
  return 'jc_' + Array.from(arr, b => b.toString(36).padStart(2, '0')).join('').slice(0, 24);
};

// CE/PE Badge Renderer
export const renderSymbolWithBadge = (symbol: string) => {
  if (!symbol) return '—';
  if (symbol.includes(' CE')) {
    const idx = symbol.indexOf(' CE');
    return <>{symbol.slice(0,idx)} <span style={{background:'#10b981',color:'#fff',padding:'2px 4px',borderRadius:4,fontSize:9,fontWeight:900,letterSpacing:1}}>CE</span>{symbol.slice(idx+3)}</>;
  }
  if (symbol.includes(' PE')) {
    const idx = symbol.indexOf(' PE');
    return <>{symbol.slice(0,idx)} <span style={{background:'#ef4444',color:'#fff',padding:'2px 4px',borderRadius:4,fontSize:9,fontWeight:900,letterSpacing:1}}>PE</span>{symbol.slice(idx+3)}</>;
  }
  return symbol;
};

// 🆕 v6.0 — Smart Notification Helper
export const notify = (title: string, body: string, type: 'success'|'error'|'info' = 'info') => {
  // Browser notification
  if ('Notification' in window && Notification.permission === 'granted') {
    new Notification(title, { body, icon: type === 'success' ? '✅' : type === 'error' ? '❌' : 'ℹ️' });
  }
  // Sound (subtle beep) — uses singleton AudioContext, was creating a fresh
  // one on every notification which leaked memory across a trading day.
  ltBeep(type === 'success' ? 880 : type === 'error' ? 220 : 440);
};
// ══════════════════════════════════════════════════════════════
//  🆕 v6.0 — SMART OPTION PRICER PANEL
//  Shows live option chain + auto-pick best strike + real CE/PE LTP
// ══════════════════════════════════════════════════════════════
// Full option-pricer universe — indices + MCX commodities + NSE F&O stocks.
// Backend resolves stock option chains via SYMBOL_MAP (see project_stock_option_pricing).
export const PRICER_SYMBOL_GROUPS: { cat: string; syms: string[] }[] = [
  { cat: 'INDEX',     syms: ['NIFTY','BANKNIFTY','FINNIFTY','MIDCPNIFTY','SENSEX','BANKEX'] },
  { cat: 'COMMODITY', syms: ['GOLD','GOLDM','SILVER','SILVERM','SILVERMIC','CRUDEOIL','CRUDEOILM','NATURALGAS','COPPER'] },
  { cat: 'STOCKS',    syms: [
      'RELIANCE','TCS','HDFCBANK','ICICIBANK','INFY','SBIN','AXISBANK','KOTAKBANK','ITC','LT',
      'BHARTIARTL','BAJFINANCE','BAJAJFINSV','HINDUNILVR','MARUTI','TATAMOTORS','TATASTEEL','M&M',
      'SUNPHARMA','HCLTECH','TECHM','WIPRO','ULTRACEMCO','TITAN','ASIANPAINT','NESTLEIND','POWERGRID',
      'NTPC','ONGC','COALINDIA','ADANIENT','ADANIPORTS','JSWSTEEL','HINDALCO','GRASIM','DRREDDY',
      'CIPLA','DIVISLAB','EICHERMOT','HEROMOTOCO','BAJAJ-AUTO','INDUSINDBK','BPCL','BRITANNIA',
      'APOLLOHOSP','TATACONSUM','UPL','SBILIFE','HDFCLIFE','JIOFIN','LTIM','LICI','DLF','VEDL',
      'PNB','BANKBARODA','CANBK','ZOMATO','PAYTM','IRCTC','DMART','PIDILITIND','HAVELLS','SIEMENS',
    ] },
];
// Flat list for legacy refs (selector now uses the grouped catalog + search).
export const PRICER_INDICES = PRICER_SYMBOL_GROUPS.flatMap(g => g.syms);

